import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledTooltip,
} from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import { useAccountDayCloses, useReopenAccountDayClose } from "hooks/useAccountDayClose"
import { formatReadableDate } from "utils/formatDate"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"

import AccountDayCloseHeader from "./AccountDayCloseHeader"

const AccountDayCloseList = () => {
  document.title = "Account Day Close History"

  const [filters, setFilters] = useState({ date_from: "", date_to: "" })
  const [reopenTarget, setReopenTarget] = useState(null)
  const [reopenReason, setReopenReason] = useState("")

  const { params, setPage, resetParams, hasActiveFilters } = useListParams({ page: 1 })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "" })
  }, [])

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10 } = params
    return {
      page,
      per_page,
      ...(filters.date_from && { date_from: filters.date_from }),
      ...(filters.date_to && { date_to: filters.date_to }),
    }
  }, [params, filters])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountDayCloses(queryParams)
  const { mutateAsync: reopenMutation, isPending: reopenLoading } = useReopenAccountDayClose()

  const toggleReopenModal = () => setReopenTarget(null)

  const handleReopenClick = item => {
    setReopenReason("")
    setReopenTarget(item)
  }

  const handleReopenSubmit = async () => {
    if (!reopenReason.trim()) return
    try {
      const res = await reopenMutation({ id: reopenTarget.id, reason: reopenReason.trim() })
      toast.success(res?.message ?? "Day reopened successfully.")
      setReopenTarget(null)
      refetch()
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to reopen the day."
      toast.error(msg)
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Close Date",
        cell: ({ row }) => formatReadableDate(row.original.close_date),
      },
      {
        header: "Closed By",
        cell: ({ row }) => row.original.closed_by?.name || row.original.closedBy?.name || "-",
      },
      {
        header: "Closed At",
        cell: ({ row }) => (row.original.created_at ? formatReadableDate(row.original.created_at) : "-"),
      },
      {
        header: "Overdue Acknowledged",
        cell: ({ row }) =>
          row.original.overdue_acknowledged ? (
            <Badge color="warning" className="text-dark">Yes</Badge>
          ) : (
            <Badge color="light" className="text-dark">No</Badge>
          ),
      },
      {
        header: "Status",
        cell: ({ row }) =>
          row.original.reopened_at ? (
            <Badge color="secondary">Reopened</Badge>
          ) : (
            <Badge color="dark">Closed</Badge>
          ),
      },
      {
        header: "Reopened",
        cell: ({ row }) =>
          row.original.reopened_at ? (
            <div className="small">
              <div>{row.original.reopened_by?.name || row.original.reopenedBy?.name || "-"}</div>
              <div className="text-muted">{formatReadableDate(row.original.reopened_at)}</div>
              <div className="text-muted fst-italic">{row.original.reopen_reason}</div>
            </div>
          ) : (
            "-"
          ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <CanView access={[ACCESS.ACCOUNT_DAY_CLOSE.REOPEN]}>
            {!row.original.reopened_at && (
              <>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => handleReopenClick(row.original)}
                  id={`reopen-account-day-close-${row.original.id}`}
                >
                  <i className="mdi mdi-lock-open-variant-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`reopen-account-day-close-${row.original.id}`}>
                  Reopen
                </UncontrolledTooltip>
              </>
            )}
          </CanView>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Account Day Close" breadcrumbItem="Day Close History">
      <AccountDayCloseHeader
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3" />

      <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset} />

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <Modal isOpen={!!reopenTarget} toggle={toggleReopenModal}>
        <ModalHeader toggle={toggleReopenModal}>
          Reopen {reopenTarget ? formatReadableDate(reopenTarget.close_date) : ""}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <Label htmlFor="reopen_reason">Reason</Label>
              <Input
                type="textarea"
                id="reopen_reason"
                value={reopenReason}
                onChange={e => setReopenReason(e.target.value)}
                invalid={!reopenReason.trim() && reopenReason !== ""}
              />
              <FormFeedback>Please provide a reason for reopening this day.</FormFeedback>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggleReopenModal}>Cancel</Button>
          <Button color="warning" onClick={handleReopenSubmit} disabled={reopenLoading || !reopenReason.trim()}>
            {reopenLoading ? "Reopening..." : "Reopen"}
          </Button>
        </ModalFooter>
      </Modal>
    </PageWrapper>
  )
}

export default AccountDayCloseList
